/**
 * itinerary — the day logic behind StopList's day headers: which days a stop
 * can move to, how to move it there, how to split a long day, and each day's
 * forecast at tonight's town.
 */
import { useEffect, useState } from 'react';
import type { TripLeg, TripStop } from '../../types/trip';
import { dateForTripDay, type TripDay } from '../../utils/tripPlanner';
import { fetchWeather, forecastForDate } from '../../utils/weatherApi';
import type { DayForecastSummary } from '../../utils/tripWeather';

/** Days a stop can move to: every driving day it isn't already part of.
 *  "Move to day 3" puts the stop on that day's drive, just before where the
 *  day ends. Middle stops only: moving the start or the destination onto
 *  another day's drive would quietly swap where the trip begins or ends. */
export function moveToDayTargets(
  stops: Pick<TripStop, 'name' | 'locked'>[],
  index: number,
  days: Pick<TripDay, 'index' | 'firstStopIndex' | 'lastStopIndex' | 'dayOffset'>[]
): { dayIndex: number; dayNumber: number; fromName: string; toName: string }[] {
  if (index <= 0 || index >= stops.length - 1 || stops[index]?.locked || days.length < 2) return [];
  return days
    .filter((d) => index < d.firstStopIndex || index > d.lastStopIndex)
    .map((d) => ({
      dayIndex: d.index,
      dayNumber: d.dayOffset + 1,
      fromName: stops[d.firstStopIndex]?.name ?? '',
      toName: stops[d.lastStopIndex]?.name ?? '',
    }));
}

/** Move a stop onto another day's drive (just before that day's last stop). */
export function moveStopToDay<T extends Pick<TripStop, 'locked'>>(
  stops: T[],
  index: number,
  day: Pick<TripDay, 'firstStopIndex' | 'lastStopIndex'>
): T[] {
  const stop = stops[index];
  if (!stop || stop.locked) return stops;
  if (index >= day.firstStopIndex && index <= day.lastStopIndex) return stops;
  const next = [...stops];
  next.splice(index, 1);
  const target = index < day.lastStopIndex ? day.lastStopIndex - 1 : day.lastStopIndex;
  next.splice(Math.max(1, target), 0, stop);
  return next;
}

/**
 * How to split a long day. If the day already passes through a stop, stay
 * the night at the one nearest halfway (by drive time). Otherwise offer to
 * insert a stop on the day's longest drive: a stop on a long run splits the
 * day by itself once it fits the daily budget.
 */
export function splitDayPlan(
  stops: Pick<TripStop, 'id' | 'locked'>[],
  legs: Pick<TripLeg, 'fromId' | 'driveMinutes'>[],
  day: Pick<TripDay, 'firstStopIndex' | 'lastStopIndex' | 'driveMinutes'>
): { kind: 'overnight'; index: number } | { kind: 'insert'; index: number } | null {
  const legFrom = new Map(legs.map((l) => [l.fromId, l]));
  const total = day.driveMinutes ?? 0;
  let elapsed = 0;
  let best: { index: number; gap: number } | null = null;
  let longest: { index: number; minutes: number } | null = null;
  for (let j = day.firstStopIndex + 1; j <= day.lastStopIndex; j += 1) {
    const minutes = legFrom.get(stops[j - 1]?.id)?.driveMinutes ?? 0;
    if (!longest || minutes > longest.minutes) longest = { index: j, minutes };
    elapsed += minutes;
    if (j < day.lastStopIndex && !stops[j]?.locked) {
      const gap = Math.abs(elapsed - total / 2);
      if (!best || gap < best.gap) best = { index: j, gap };
    }
  }
  if (best) return { kind: 'overnight', index: best.index };
  return longest ? { kind: 'insert', index: longest.index } : null;
}

/** The day a stop is reached on (its arrival), or Day 1 for the start. */
export function dayOfStop<D extends Pick<TripDay, 'firstStopIndex' | 'lastStopIndex'>>(days: D[], index: number): D | undefined {
  if (index === 0) return days[0];
  return days.find((d) => index > d.firstStopIndex && index <= d.lastStopIndex);
}

/** Each driving day's forecast at tonight's town, once the trip has a date. */
export function useDayForecasts(
  days: TripDay[],
  stops: Pick<TripStop, 'lat' | 'lng'>[],
  startDate: string | null | undefined
): Record<number, DayForecastSummary | null> {
  const [forecasts, setForecasts] = useState<Record<number, DayForecastSummary | null>>({});
  // Refetch when the overnight places or dates change, not on every render.
  const key = startDate
    ? days
        .map((d) => {
          const s = stops[d.lastStopIndex];
          return `${d.index}:${d.dayOffset}:${s?.lat?.toFixed(2)},${s?.lng?.toFixed(2)}`;
        })
        .join('|')
    : '';
  useEffect(() => {
    if (!startDate || !days.length) {
      setForecasts({});
      return;
    }
    let cancelled = false;
    days.forEach((day) => {
      const date = dateForTripDay(startDate, day.dayOffset);
      const overnight = stops[day.lastStopIndex];
      if (!date || !overnight || typeof overnight.lat !== 'number' || typeof overnight.lng !== 'number') return;
      fetchWeather(overnight.lat, overnight.lng)
        .then((w) => {
          if (cancelled) return;
          const f = forecastForDate(w, date);
          setForecasts((prev) => ({
            ...prev,
            [day.index]: f
              ? {
                  condition: f.condition,
                  max: f.temp_max_c,
                  min: f.temp_min_c,
                  rainChancePct: f.rain_chance_pct,
                  rainMm: f.rain_mm,
                  windKmh: f.wind_max_kmh,
                }
              : null,
          }));
        })
        .catch(() => {
          /* no forecast: the band just shows no weather */
        });
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return forecasts;
}
